// Storage types and interfaces for route persistence

export interface RoutePoint {
  id?: string;
  lat: number;
  lng: number;
  orderIndex: number;
  // Address information to avoid repeated reverse geocoding API calls
  address_street?: string;
  address_house_number?: string;
  address_city?: string;
  address_suburb?: string;
  address_country?: string;
  address_display_name?: string;
  address_raw_data?: any; // Store original Nominatim data for future use
}

// Full route data from routing API (BRouter response)
export interface RouteFullData {
  id?: string;
  route_id: string;
  route_geometry: GeoJSON.LineString; // Full GeoJSON with all coordinates
  route_properties?: {
    // Core BRouter response properties
    'track-length'?: number;
    'total-time'?: number;
    'filtered ascend'?: number;
    messages?: string[]; // Contains surface, road type, and other detailed info
    creator?: string;
    name?: string;
    // Additional processed data for frontend
    elevation_profile?: number[];
    surface_types?: string[];
    road_types?: string[];
    segments?: any[]; // Processed segments from messages
    rawSegments?: any[]; // Raw segments from messages
    // Store the complete original BRouter feature properties
    originalBRouterProperties?: any;
    [key: string]: any;
  };
  routing_engine?: string;
  routing_profile: string;
  routing_version?: string;
  routing_timestamp?: Date;
  coordinates_count?: number;
  is_compressed?: boolean;
  compression_algorithm?: string;
  created_at?: Date;
  updated_at?: Date;
}

export interface Route {
  id?: string;
  name: string;
  bikeProfile: string;
  userId: string;
  created?: Date;
  updated?: Date;
  deleted?: boolean;
  isPublic?: boolean; // Route visibility setting
  points: RoutePoint[];
  // Route statistics (calculated when route is saved)
  distance?: number; // in meters
  duration?: number; // in seconds
  ascend?: number; // elevation gain in meters
  // Power calculator settings (saved with route)
  powerToWeight?: number; // W/kg ratio (1.0 - 4.0)
  showOriginal?: boolean; // Auto mode (true) or manual power (false)
  // Thumbnail image paths in Supabase Storage
  thumbnail_path?: string;
  elevation_thumbnail_path?: string;
  // GPX Upload fields
  sourceType?: 'manual' | 'gpx_import' | 'gpx_optimized';
  originalFilename?: string;
  gpxMetadata?: {
    filename?: string;
    trackCount?: number;
    totalPoints?: number;
    deviceInfo?: any;
    timestamps?: {
      start?: string;
      end?: string;
    };
    originalBounds?: {
      minLat: number;
      maxLat: number;
      minLng: number;
      maxLng: number;
    };
  };
  // Full route data (optional, loaded separately for performance)
  fullRouteData?: RouteFullData;
}

export interface StorageResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface RouteStorageProvider {
  saveRoute(route: Omit<Route, 'id' | 'created'>): Promise<StorageResult<Route>>;
  getRoute(id: string, includeFullData?: boolean): Promise<StorageResult<Route>>;
  getRoutes(userId: string): Promise<StorageResult<Route[]>>;
  deleteRoute(id: string): Promise<StorageResult<boolean>>;
  updateRoute(id: string, route: Partial<Route>): Promise<StorageResult<Route>>;
  updateRoutePoint(routeId: string, pointId: string, pointData: Partial<RoutePoint>): Promise<StorageResult<RoutePoint>>;
  // New methods for full route data
  saveFullRouteData(routeId: string, fullData: Omit<RouteFullData, 'id' | 'route_id' | 'created_at' | 'updated_at'>): Promise<StorageResult<RouteFullData>>;
  getFullRouteData(routeId: string): Promise<StorageResult<RouteFullData | null>>;
  updateFullRouteData(routeId: string, fullData: Partial<RouteFullData>): Promise<StorageResult<RouteFullData>>;
}

export const StorageType = {
  SUPABASE: 'supabase',
  LOCAL_STORAGE: 'localStorage',
  MEMORY: 'memory'
} as const;

export type StorageType = typeof StorageType[keyof typeof StorageType];

export interface StorageConfig {
  type: StorageType;
  endpoint?: string;
  apiKey?: string;
  userId?: string;
}
